'use client';

import dynamic from 'next/dynamic';
import { ComponentProps, useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import TrailDetailsActionLocation from '@alltrails/amplitude/enums/TrailDetailsActionLocation';
import logGarminConnectSucceeded from '@alltrails/amplitude/events/logGarminConnectSucceeded';
import logTrailDetailsHitTheTrailButtonClicked from '@alltrails/amplitude/events/logTrailDetailsHitTheTrailButtonClicked';
import type TrailDetails from '@alltrails/types/TrailDetails';
import Button from '@alltrails/denali/components/Button';
import type { Size } from '@alltrails/denali/types';
import { TrailPage } from '@alltrails/trails';
import styles from './DesktopCtas.module.scss';
import { DirectionsCta } from '@alltrails/trails';

const HitTheTrailModal = dynamic(() => import('@/components/HitTheTrailModal/HitTheTrailModal'), { ssr: false });

type DesktopCtasProps = {
  size?: Size<'sm' | 'md'>;
  trailDetails: TrailDetails;
  trailPage: TrailPage;
};

type HitTheTrailButtonProps = {
  hitTheTrailButtonLocation: TrailDetailsActionLocation;
  setIsHitTheTrailModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
  size?: Size<'sm' | 'md'>;
};

function HitTheTrailButton({ hitTheTrailButtonLocation, size, setIsHitTheTrailModalOpen }: HitTheTrailButtonProps) {
  return (
    <Button
      size={size}
      text={<FormattedMessage defaultMessage="Hit the trail" />}
      variant="accent"
      onClick={() => {
        logTrailDetailsHitTheTrailButtonClicked({ hit_the_trail_button_location: hitTheTrailButtonLocation });
        setIsHitTheTrailModalOpen(true);
      }}
    />
  );
}

const DesktopCtas = ({ size = 'md', trailDetails, trailPage }: DesktopCtasProps) => {
  const intl = useIntl();
  const { id, location } = trailDetails;
  const [isHitTheTrailModalOpen, setIsHitTheTrailModalOpen] = useState(false);
  const [steps, setSteps] = useState<ComponentProps<typeof HitTheTrailModal>['steps']>();

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);

    if (params.has('garmin_permissions_return')) {
      logGarminConnectSucceeded({ trail_id: trailDetails.id });

      setSteps(['initial', 'garmin']);
      setIsHitTheTrailModalOpen(true);
    }
  }, [trailDetails.id]);

  return (
    <div className={styles.desktopCtasContainer}>
      <div className={styles.floating} role="region" aria-label={intl.formatMessage({ defaultMessage: 'Choose the trail' })}>
        <DirectionsCta buttonLocation="bottom floating bar light" location={location} trailId={id} size={size} displayAsButton />
        <HitTheTrailButton hitTheTrailButtonLocation="bottom floating bar light" setIsHitTheTrailModalOpen={setIsHitTheTrailModalOpen} size={size} />
      </div>
      {isHitTheTrailModalOpen && (
        <HitTheTrailModal
          includeOpenInApp
          isOpen={isHitTheTrailModalOpen}
          onCloseRequest={() => {
            setIsHitTheTrailModalOpen(false);
          }}
          steps={steps}
          title={<FormattedMessage defaultMessage="Hit the trail" />}
          trailDetails={trailDetails}
          trailPage={trailPage}
        />
      )}
    </div>
  );
};

export default DesktopCtas;
